import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CloudRain, FileArrowDown, Lock, ShieldCheck, Clock, MapPin, CalendarBlank, Warning } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;

const SharedReportView = () => {
  const { token } = useParams();
  const [reportMeta, setReportMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchMeta();
  }, [token]);

  const fetchMeta = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/shared/${token}`);
      setReportMeta(data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) setError('This shared link was not found or has been removed.');
      else if (status === 410) setError(err.response?.data?.detail || 'This link has expired or been revoked.');
      else setError('Unable to load this report.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/shared/${token}/download`,
        reportMeta.password_protected ? { password } : {},
      );
      const byteArray = new Uint8Array(data.pdf_data.match(/.{1,2}/g).map(b => parseInt(b, 16)));
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `WeatherProof-${data.report_id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Incorrect password');
      } else {
        toast.error(err.response?.data?.detail || 'Download failed');
      }
    } finally {
      setDownloading(false);
    }
  };

  const MODE_LABELS = { single: 'Single Query', range: 'Date Range', last24h: 'Last 24 Hours' };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <CloudRain size={28} weight="bold" className="text-blue-600" />
            <span className="text-xl font-bold tracking-tight text-slate-950" style={{ fontFamily: 'Chivo' }}>WeatherProof</span>
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : error ? (
          <div className="bg-white border border-slate-200 p-8 text-center" data-testid="share-error">
            <Warning size={40} weight="light" className="text-slate-300 mx-auto mb-4" />
            <p className="text-sm text-slate-600 mb-6">{error}</p>
            <Link to="/" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              Go to WeatherProof
            </Link>
          </div>
        ) : reportMeta ? (
          <div className="space-y-5" data-testid="shared-report-view">
            {/* Report Card */}
            <div className="bg-white border border-slate-200">
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck size={18} weight="bold" className="text-blue-600" />
                  <p className="text-xs font-medium uppercase tracking-[0.15em] text-blue-600">Shared Weather Report</p>
                </div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-950" style={{ fontFamily: 'Chivo' }}>
                  {reportMeta.location || 'Weather Verification Report'}
                </h1>
              </div>

              <div className="grid grid-cols-2 gap-px bg-slate-100">
                <div className="bg-white p-4">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-slate-400 mb-1">Report ID</p>
                  <p className="text-xs font-mono text-slate-700" data-testid="shared-report-id">{reportMeta.report_id}</p>
                </div>
                <div className="bg-white p-4">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-slate-400 mb-1">Generated</p>
                  <p className="text-xs font-mono text-slate-700" data-testid="shared-report-date">
                    {reportMeta.created_at ? new Date(reportMeta.created_at).toLocaleString() : 'N/A'}
                  </p>
                </div>
                {reportMeta.mode && (
                  <div className="bg-white p-4">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-slate-400 mb-1">Query Type</p>
                    <p className="text-xs text-slate-700">{MODE_LABELS[reportMeta.mode] || reportMeta.mode}</p>
                  </div>
                )}
                {reportMeta.use_case && (
                  <div className="bg-white p-4">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-slate-400 mb-1">Purpose</p>
                    <p className="text-xs text-slate-700 capitalize">{reportMeta.use_case}</p>
                  </div>
                )}
              </div>

              {reportMeta.expires_at && (
                <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 flex items-center gap-1.5">
                  <Clock size={12} className="text-slate-400" />
                  <p className="text-[10px] text-slate-500">
                    Link expires: {new Date(reportMeta.expires_at).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>

            {/* Download Section */}
            <div className="bg-white border border-slate-200 p-6">
              {reportMeta.password_protected && (
                <div className="mb-5">
                  <label className="block text-xs font-medium uppercase tracking-[0.15em] text-slate-500 mb-2">
                    <Lock size={12} className="inline mr-1" />
                    This report is password protected
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password to download"
                    data-testid="shared-password-input"
                    className="w-full px-3 py-2 border border-slate-300 text-sm focus:ring-2 focus:ring-blue-600 focus:ring-offset-1 focus:outline-none"
                  />
                </div>
              )}

              <button
                onClick={handleDownload}
                disabled={downloading || (reportMeta.password_protected && !password.trim())}
                data-testid="shared-download-btn"
                className="w-full py-3 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors text-sm font-medium flex items-center justify-center gap-2"
              >
                <FileArrowDown size={20} weight="bold" />
                {downloading ? 'Downloading...' : 'Download PDF Report'}
              </button>
            </div>

            {/* Trust Footer */}
            <p className="text-center text-[10px] text-slate-400 leading-relaxed">
              This report was generated by WeatherProof using historical weather data from third-party providers.<br />
              Report authenticity can be verified at <Link to="/verify-report" className="text-blue-500 hover:underline">weatherproof.com/verify-report</Link>
            </p>
          </div>
        ) : null}
      </main>
    </div>
  );
};

export default SharedReportView;
